import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Button, InputNumber, Divider, Space, Tooltip, Alert, Select } from 'antd';
import { SendOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../../hooks/useTranslationWithNamespace';
import { useCreateWithdrawal } from '../../../hooks/useTransactionManagement';
import { showSuccessNotification, showErrorNotification } from '../../../utils/notifications';
import { estimateFee, getAllWalletBalances } from '../../../api';
import WithdrawalModalSpeedSelector from './WithdrawalModalSpeedSelector';
import WithdrawalModalPrivateKeyField from './WithdrawalModalPrivateKeyField';
import WithdrawalModalFeeEstimation from './WithdrawalModalFeeEstimation';
import { WithdrawalModalProps, LastEstimationInputs, WithdrawalFormValues, WithdrawalApiData } from './WithdrawalModalTypes';
import { formatAmount, isValidAmount, shouldUpdateFeeEstimation } from './WithdrawalModalUtils';
import './WithdrawalModal.css';

const { Option } = Select;

// Typography components are used in sub-components

/**
 * Modal for creating a withdrawal transaction
 * Enhanced with features from the legacy RPC dashboard
 */
const WithdrawalModal: React.FC<WithdrawalModalProps> = ({
  visible,
  onCancel,
  walletData
}) => {
  const { t } = useTranslationWithNamespace('wallet');
  const [form] = Form.useForm();
  const [isEstimatingFee, setIsEstimatingFee] = useState(false);
  const [feeEstimation, setFeeEstimation] = useState<any>(null);
  const [feeEstimationError, setFeeEstimationError] = useState<string | null>(null);
  const [selectedSpeed, setSelectedSpeed] = useState<'slow' | 'medium' | 'fast'>('medium');
  const [shouldEstimateFee, setShouldEstimateFee] = useState(false);
  const [availableTokens, setAvailableTokens] = useState<any[]>([]);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [selectedToken, setSelectedToken] = useState<string>('');

  // Mutation for creating a withdrawal
  const createWithdrawalMutation = useCreateWithdrawal();

  // Handle token selection change
  const handleTokenChange = (value: string) => {
    setSelectedToken(value);

    // Find the selected token details
    const selectedTokenData = availableTokens.find(token => token.value === value);

    if (selectedTokenData) {
      // Update the coin field to reflect the selected token
      form.setFieldsValue({
        coin: selectedTokenData.symbol,
        token: value === 'native' ? '' : value
      });
    }
  };

  // Track last estimation inputs to avoid unnecessary API calls
  const [lastEstimationInputs, setLastEstimationInputs] = useState<LastEstimationInputs>({
    amount: '',
    to_address: '',
    token: '',
    speed: 'medium',
    timestamp: 0
  });

  // Fetch available tokens when modal becomes visible
  const fetchAvailableTokens = useCallback(async () => {
    if (!walletData) return;

    setIsLoadingTokens(true);
    try {
      console.log('Fetching available tokens for wallet:', walletData);

      const response = await getAllWalletBalances(
        walletData.address,
        walletData.network,
        walletData.is_evm || 0
      );

      console.log('Available tokens response:', response);

      if (response.status && response.data && response.data.balances) {
        const tokens = response.data.balances.map((balance: any) => ({
          symbol: balance.symbol,
          contract_address: balance.contract_address,
          type: balance.type,
          balance: balance.balance,
          label: balance.type === 'native'
            ? `${balance.symbol} (Native)`
            : `${balance.symbol} (Token)`,
          value: balance.contract_address || 'native'
        }));

        setAvailableTokens(tokens);
        console.log('Processed tokens:', tokens);
      } else {
        console.warn('No tokens found or invalid response:', response);
        setAvailableTokens([]);
      }
    } catch (error) {
      console.error('Error fetching available tokens:', error);
      setAvailableTokens([]);
    } finally {
      setIsLoadingTokens(false);
    }
  }, [walletData]);

  // Reset form when modal becomes visible or wallet data changes
  useEffect(() => {
    if (visible && walletData) {
      form.resetFields();
      form.setFieldsValue({
        from_address: walletData.address,
        coin: walletData.coin,
        network: walletData.network,
        user_id: walletData.user_id,
        speed: 'medium'
      });
      setFeeEstimation(null);
      setFeeEstimationError(null);
      setSelectedSpeed('medium');
      setSelectedToken('native'); // Default to native token

      // Reset last estimation inputs
      setLastEstimationInputs({
        amount: '',
        to_address: '',
        token: '',
        speed: 'medium',
        timestamp: 0
      });

      // Fetch available tokens
      fetchAvailableTokens();
    }
  }, [visible, walletData, form, fetchAvailableTokens]);

  // Update coin field when tokens are loaded and default is set
  useEffect(() => {
    if (availableTokens.length > 0 && selectedToken === 'native') {
      const nativeToken = availableTokens.find(token => token.type === 'native');
      if (nativeToken) {
        form.setFieldsValue({ coin: nativeToken.symbol });
      }
    }
  }, [availableTokens, selectedToken, form]);

  // Fetch fee estimation from API
  const fetchFeeEstimation = useCallback(async () => {
    try {
      const values = form.getFieldsValue();
      const { amount, to_address, coin, network, token = '' } = values;

      // Skip if any required field is missing
      if (!amount || !to_address || !coin || !network || !walletData?.address) {
        return;
      }

      // Check if we need to update the fee estimation
      const currentInputs = {
        amount: amount.toString(),
        to_address,
        token: token || '',
        speed: selectedSpeed
      };

      if (!shouldUpdateFeeEstimation(currentInputs, lastEstimationInputs)) {
        return;
      }

      // Update last estimation inputs
      setLastEstimationInputs({
        ...currentInputs,
        timestamp: Date.now()
      });

      setIsEstimatingFee(true);
      setFeeEstimationError(null);

      // Format amount properly
      let formattedAmount = formatAmount(amount);

      // Call fee estimation API
      const result = await estimateFee(
        network,
        walletData.address,
        to_address,
        formattedAmount,
        selectedSpeed,
        token
      );

      if (result.status || result.success) {
        setFeeEstimation(result.data || result);
      } else {
        setFeeEstimationError(result.error || 'Failed to estimate fee');
      }
    } catch (error) {
      console.error('Error estimating fee:', error);
      setFeeEstimationError(
        error instanceof Error ? error.message : 'Unknown error estimating fee'
      );
    } finally {
      setIsEstimatingFee(false);
    }
  }, [form, lastEstimationInputs, selectedSpeed, walletData]);

  // Trigger fee estimation when both amount and to_address are filled
  useEffect(() => {
    if (!visible || !form) return;

    if (shouldEstimateFee) {
      const debouncedEstimate = setTimeout(() => {
        fetchFeeEstimation();
        setShouldEstimateFee(false);
      }, 500);

      return () => clearTimeout(debouncedEstimate);
    }
  }, [form, shouldEstimateFee, visible, fetchFeeEstimation]);

  // Handle form field changes
  const handleFormChange = () => {
    const amount = form.getFieldValue('amount');
    const to_address = form.getFieldValue('to_address');

    if (amount && to_address) {
      setShouldEstimateFee(true);
    }
  };

  // Handle speed change
  const handleSpeedChange = (value: 'slow' | 'medium' | 'fast') => {
    setSelectedSpeed(value);
    form.setFieldsValue({ speed: value });

    // Trigger fee estimation if we have the required fields
    if (form.getFieldValue('amount') && form.getFieldValue('to_address')) {
      setShouldEstimateFee(true);
    }
  };

  // Manually trigger fee estimation
  const triggerFeeEstimation = () => {
    setShouldEstimateFee(true);
  };

  // Handle form submission
  const handleSubmit = async (values: WithdrawalFormValues) => {
    try {
      // Validate required fields
      if (!values.from_address || !values.to_address || !values.amount || !values.network || !values.coin) {
        showErrorNotification(
          t('withdrawal.validationError', 'Validation Error'),
          t('withdrawal.fillRequiredFields', 'Please fill all required fields')
        );
        return;
      }

      // Validate amount
      if (!isValidAmount(values.amount)) {
        showErrorNotification(
          t('withdrawal.invalidAmount', 'Invalid Amount'),
          t('withdrawal.amountMustBeNumber', 'Amount must be a valid number')
        );
        return;
      }

      // Format amount properly
      const formattedAmount = formatAmount(values.amount);

      console.log('Creating withdrawal with amount:', formattedAmount);

      // Prepare data for API call
      const withdrawalData: WithdrawalApiData = {
        from_address: values.from_address,
        to: values.to_address,
        amount: formattedAmount,
        coin: values.coin,
        network: values.network,
        user_id: Number(values.user_id), // Convert to number
        priority: values.speed || 'medium'
      };

      // Add optional fields if provided
      if (values.tag) {
        withdrawalData.tag = values.tag;
      }

      // Handle token selection
      if (selectedToken && selectedToken !== 'native') {
        withdrawalData.contract_address = selectedToken;
      }

      if (values.private_key) {
        withdrawalData.privateKey = values.private_key;
      }

      // Send withdrawal request
      const response = await createWithdrawalMutation.mutateAsync(withdrawalData);

      console.log('Withdrawal response:', response);

      // Check if the response indicates success
      if (response && response.status === true) {
        showSuccessNotification(
          t('withdrawal.success', 'Withdrawal Created'),
          t('withdrawal.successMessage', 'Your withdrawal request has been created successfully')
        );
        onCancel();
      } else {
        // Handle error response
        let errorMessage = '';

        // Check if it's a validation error with a friendly message
        if (response?.errorType === 'VALIDATION_ERROR' && response?.friendlyMessage) {
          errorMessage = response.friendlyMessage;

          // Log the detailed error for debugging
          console.error('Validation error details:', response.error);
        }
        // Check if it's a validation error without a friendly message
        else if (response?.error && typeof response.error === 'string' && response.error.includes('validation failed')) {
          // Format validation error message to be more user-friendly
          errorMessage = t('withdrawal.validationError', 'Validation Error: Please check your input');

          // Log the detailed error for debugging
          console.error('Validation error details:', response.error);
        } else {
          // Handle other types of errors
          errorMessage = response?.error ||
            (response?.error?.message ? response.error.message : null) ||
            (response?.error?.details?.originalMessage ? response.error.details.originalMessage : null) ||
            response?.data ||
            t('withdrawal.unknownError', 'Unknown error');
        }

        showErrorNotification(
          t('withdrawal.error', 'Failed to Create Withdrawal'),
          errorMessage
        );
      }
    } catch (error) {
      console.error('Error creating withdrawal:', error);

      // Extract error message from Axios error if possible
      let errorMessage = 'Unknown error';

      if (error instanceof Error) {
        errorMessage = error.message;

        // Check if it's an Axios error with response data
        const axiosError = error as any;
        if (axiosError.response?.data) {
          const responseData = axiosError.response.data;

          // Check if it's a validation error with a friendly message
          if (responseData.errorType === 'VALIDATION_ERROR' && responseData.friendlyMessage) {
            errorMessage = responseData.friendlyMessage;
            console.error('Validation error details:', responseData.error);
          }
          // Check if it's a validation error without a friendly message
          else if (responseData.error && typeof responseData.error === 'string' && responseData.error.includes('validation failed')) {
            errorMessage = t('withdrawal.validationError', 'Validation Error: Please check your input');
            console.error('Validation error details:', responseData.error);
          } else {
            // Use the error message from the API response if available
            errorMessage = responseData.error || responseData.message || errorMessage;
          }
        }
      }

      showErrorNotification(
        t('withdrawal.error', 'Failed to Create Withdrawal'),
        errorMessage
      );
    }
  };

  return (
    <Modal
      title={
        <Space>
          <SendOutlined />
          {t('withdrawal.title', 'Create Withdrawal')}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('common.cancel', 'Cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<SendOutlined />}
          onClick={() => form.submit()}
          loading={createWithdrawalMutation.isPending}
        >
          {t('withdrawal.create', 'Create Withdrawal')}
        </Button>
      ]}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
        initialValues={{
          speed: 'medium'
        }}
      >
        <Form.Item
          name="from_address"
          label={t('sendTransaction.fromAddress', 'From Address')}
          rules={[{ required: true, message: t('sendTransaction.fromAddressRequired', 'From address is required') }]}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="to_address"
          label={t('sendTransaction.toAddress', 'To Address')}
          rules={[{ required: true, message: t('sendTransaction.toAddressRequired', 'To address is required') }]}
        >
          <Input placeholder={t('sendTransaction.toAddressPlaceholder', 'Enter destination address')} />
        </Form.Item>

        <Form.Item
          name="amount"
          label={t('sendTransaction.amount', 'Amount')}
          rules={[{ required: true, message: t('sendTransaction.amountRequired', 'Amount is required') }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder={t('sendTransaction.amountPlaceholder', 'Enter amount to send')}
            stringMode
          />
        </Form.Item>

        <Form.Item
          name="network"
          label={t('sendTransaction.network', 'Network')}
          rules={[{ required: true, message: t('sendTransaction.networkRequired', 'Network is required') }]}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          name="coin"
          label={t('sendTransaction.coin', 'Selected Coin/Token')}
        >
          <Input disabled style={{ backgroundColor: '#f0f0f0' }} />
        </Form.Item>

        <Form.Item
          label={
            <span>
              {t('sendTransaction.token', 'Token/Coin')}
              <Tooltip title={t('sendTransaction.tokenTooltip', 'Select the token or native coin you want to send.')}>
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </span>
          }
          rules={[{ required: true, message: t('sendTransaction.tokenRequired', 'Token selection is required') }]}
        >
          <Select
            value={selectedToken}
            onChange={handleTokenChange}
            placeholder={t('sendTransaction.selectToken', 'Select token or coin')}
            loading={isLoadingTokens}
            style={{ width: '100%' }}
          >
            {availableTokens.map((token) => (
              <Option key={token.value} value={token.value}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{token.label}</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    Balance: {parseFloat(token.balance).toFixed(6)}
                  </span>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Hidden field for backward compatibility with fee estimation */}
        <Form.Item name="token" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          name="tag"
          label={
            <span>
              {t('sendTransaction.tag', 'Tag/Memo')}
              <Tooltip title={t('sendTransaction.tagTooltip', 'Some blockchains require a tag or memo for transfers to exchanges.')}>
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </span>
          }
        >
          <Input placeholder={t('sendTransaction.tagPlaceholder', 'Enter tag or memo (optional)')} />
        </Form.Item>

        <Form.Item
          name="user_id"
          label={t('sendTransaction.userId', 'User ID')}
          rules={[{ required: true, message: t('sendTransaction.userIdRequired', 'User ID is required') }]}
          hidden
        >
          <Input type="hidden" />
        </Form.Item>

        <Divider>{t('sendTransaction.transactionSpeed', 'Transaction Speed')}</Divider>

        <div style={{ marginBottom: 16 }}>
          <WithdrawalModalSpeedSelector
            selectedSpeed={selectedSpeed}
            handleSpeedChange={handleSpeedChange}
          />
        </div>

        {/* Fee Estimation Component */}
        <div style={{ marginBottom: 16 }}>
          <WithdrawalModalFeeEstimation
            isEstimatingFee={isEstimatingFee}
            feeEstimation={feeEstimation}
            feeEstimationError={feeEstimationError}
            triggerFeeEstimation={triggerFeeEstimation}
            form={form}
          />
        </div>

        {/* Private Key Field Component */}
        <WithdrawalModalPrivateKeyField
          form={form}
          walletAddress={walletData?.address || ''}
        />

        <Alert
          message={t('withdrawal.securityInfo', 'Security Information')}
          description={t('withdrawal.securityInfoText', 'This withdrawal will be processed using the private key stored securely in the database. You can optionally provide a private key to override this behavior.')}
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />

        <Alert
          message={t('withdrawal.transactionWarning', 'Transaction Warning')}
          description={t('withdrawal.transactionWarningText', 'Cryptocurrency transactions are irreversible. Please double-check the recipient address and amount before proceeding.')}
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Form>
    </Modal>
  );
};

export default WithdrawalModal;
